import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AccountLayout } from 'src/sections/account/account-layout';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';
import {
  LegacyContentRedirect,
  LegacyWorksheetRedirect,
  DashboardWorkspaceRedirect,
  LegacyContentEditorRedirect,
  LegacyWorksheetTestRedirect,
} from '../components/workspace-redirect';

// ----------------------------------------------------------------------

// Overview
const OverviewPage = lazy(() => import('src/pages/dashboard/overview'));
// Account
const AccountGeneralPage = lazy(() => import('src/pages/dashboard/user/account/general'));
const AccountChangePasswordPage = lazy(
  () => import('src/pages/dashboard/user/account/change-password')
);
const AccountNotificationsPage = lazy(
  () => import('src/pages/dashboard/user/account/notifications')
);
const AccountSubscriptionPage = lazy(() => import('src/pages/dashboard/user/account/subscription'));
const AccountLinkedAccountsPage = lazy(
  () => import('src/pages/dashboard/user/account/linked-accounts')
);
// Seet
const SeetPage = lazy(() => import('src/pages/seet'));
const DashboardSeetPage = lazy(() => import('src/pages/seet/dashboard-seet'));
const ContentPage = lazy(() => import('src/pages/dashboard/content'));
const ContentEditorPage = lazy(() => import('src/pages/dashboard/content/editor'));
const H5PContentEditorPage = lazy(() => import('src/pages/dashboard/content/edit'));
const WorksheetEditorPage = lazy(() => import('src/pages/dashboard/worksheet/worksheetId'));
const WorksheetTestPage = lazy(() => import('src/pages/dashboard/worksheet/test'));
// Groups
const GroupsPage = lazy(() => import('src/pages/dashboard/groups'));
const GroupLayoutWrapperPage = lazy(
  () => import('src/pages/dashboard/groups/group-layout-wrapper')
);
const SeatingViewPage = lazy(() => import('src/pages/dashboard/groups/seating-view'));
const AnalyticsViewPage = lazy(() => import('src/pages/dashboard/groups/analytics-view'));
const GroupSettingsPage = lazy(() => import('src/pages/dashboard/groups/settings'));
const AssignmentViewerPage = lazy(() => import('src/pages/dashboard/groups/assignment-viewer'));
// Learners
const LearnersPage = lazy(() => import('src/pages/dashboard/learners'));
const LearnerDetailPage = lazy(() => import('src/pages/dashboard/learners/detail'));
// Deployments
const DeploymentsPage = lazy(() => import('src/pages/dashboard/deployments'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

const accountLayout = () => (
  <AccountLayout>
    <SuspenseOutlet />
  </AccountLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { index: true, element: <DashboardWorkspaceRedirect /> },
      { path: 'overview', element: <OverviewPage /> },
      {
        path: 'user',
        children: [
          {
            path: 'account',
            element: accountLayout(),
            children: [
              { index: true, element: <AccountGeneralPage /> },
              {
                path: 'security',
                element: <AccountChangePasswordPage />,
              },
              {
                path: 'notifications',
                element: <AccountNotificationsPage />,
              },
              {
                path: 'linked-accounts',
                element: <AccountLinkedAccountsPage />,
              },
              {
                path: 'subscription',
                element: <AccountSubscriptionPage />,
              },
            ],
          },
        ],
      },
      { path: 'seet', element: <SeetPage /> },
      { path: 'seet/:seetId', element: <DashboardSeetPage /> },
      {
        // New workspace-based routes
        path: 'workspace/:workspaceId/folder/:folderId/content',
        element: <ContentPage />,
      },
      {
        path: 'workspace/:workspaceId/folder/:folderId/content/:contentId',
        element: <ContentEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/folder/:folderId/content/:contentId/edit',
        element: <H5PContentEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/worksheet/:worksheetId/edit',
        element: <WorksheetEditorPage />,
      },
      {
        path: 'workspace/:workspaceId/worksheet/test',
        element: <WorksheetTestPage />,
      },
      // Legacy redirects
      { path: 'content', element: <LegacyContentRedirect /> },
      { path: 'content/:contentId', element: <LegacyContentEditorRedirect /> },
      {
        path: 'content/:contentId/edit',
        element: <LegacyContentEditorRedirect />,
      },
      {
        path: 'worksheet/:worksheetId/edit',
        element: <LegacyWorksheetRedirect />,
      },
      { path: 'worksheet/test', element: <LegacyWorksheetTestRedirect /> },
      { path: 'groups', element: <GroupsPage /> },
      {
        path: 'groups/:groupId',
        element: <GroupLayoutWrapperPage />,
        children: [
          { index: true, element: <Navigate to="seating" replace /> },
          { path: 'seating', element: <SeatingViewPage /> },
          { path: 'analytics', element: <AnalyticsViewPage /> },
          { path: 'settings', element: <GroupSettingsPage /> },
        ],
      },
      {
        path: 'groups/:groupId/assignments/:assignmentId',
        element: <AssignmentViewerPage />,
      },
      { path: 'learners', element: <LearnersPage /> },
      { path: 'learners/:learnerId', element: <LearnerDetailPage /> },
      { path: 'deployments', element: <DeploymentsPage /> },
      { path: 'deployments/:deploymentId', element: <DeploymentsPage /> },
      { path: 'deployments/:deploymentId/:tab', element: <DeploymentsPage /> },
    ],
  },
];
